import { InvestmentRequest } from 'client/cip/_openapi/portfolio-optimizer/models/InvestmentRequest';
import { useMemo } from 'react';

import { Account, LifecycleStatus } from '@ndvr/portfolio-api';

import { FORMER_STAFF, useInternalUserProfilesQuery, useKnownActorName } from '@/modules/internal-user-queries';
import { useListAccountsByLifecycleStatus, useListAccountsForTenant } from 'views/portfolio/account-queries';

import { useInvestmentRequest } from './investment-request-queries';
import { InvestmentRequestTableEntry } from './InvestmentRequestListPage';

/**
 * Options for useInvestmentRequestData hook
 */
export interface UseInvestmentRequestDataOptions {
    /**
     * Whether to filter investment requests by tenant accounts
     * Used in list view when user doesn't have custody access
     */
    filterByTenant?: boolean;
}

/**
 * Result from useInvestmentRequestData hook
 */
export interface UseInvestmentRequestDataResult {
    /** All investment requests transformed with account and person data */
    investmentRequests: InvestmentRequestTableEntry[] | undefined;

    /** Helper to get a single transformed request by ID */
    getInvestmentRequestById: (requestId: number) => InvestmentRequestTableEntry | undefined;

    /** Combined loading state from all data sources */
    isLoading: boolean;

    /** Whether data is being refetched in the background */
    isUpdating: boolean;

    /** Individual loading states for granular control */
    loadingStates: {
        isLoadingRequests: boolean;
        isLoadingAccounts: boolean;
        isLoadingPersons: boolean;
        isLoadingTenantAccounts: boolean;
    };

    /** Raw data maps for cases where direct access is needed */
    rawData: {
        accounts: Account[] | undefined;
        accountMap: Map<string, Account>;
        tenantAccounts: Account[] | undefined;
    };
}

/**
 * Custom hook for fetching and transforming investment request data
 * Combines investment requests with account and person data
 *
 * @param options - Configuration options for the hook
 * @returns Transformed investment request data with helper functions
 *
 * @example
 * // Basic usage - get all investment requests
 * const { investmentRequests, isLoading } = useInvestmentRequestData();
 *
 * @example
 * // Get a specific investment request
 * const { getInvestmentRequestById, isLoading } = useInvestmentRequestData();
 * const investmentRequest = getInvestmentRequestById(123);
 *
 * @example
 * // Filter by tenant (for users without custody access)
 * const { investmentRequests } = useInvestmentRequestData({ filterByTenant: true });
 *
 * TODO: Future optimization - Add support for fetching single investment request
 * When only viewing a single request (e.g., in the drawer), we currently fetch
 * all requests and filter client-side. A future enhancement could add an API
 * endpoint like `getInvestmentRequestById(id)` that returns a single Investment Request.
 * This would significantly improve performance for deep-linked drawer views.
 */
export function useInvestmentRequestData(
    options: UseInvestmentRequestDataOptions = {},
): UseInvestmentRequestDataResult {
    const { filterByTenant = false } = options;

    // Fetch all required data
    const {
        data: rawInvestmentRequests,
        isLoading: isLoadingRequests,
        isFetching: isFetchingRequests,
    } = useInvestmentRequest();
    const { data: accounts, isLoading: isLoadingAccounts } = useListAccountsByLifecycleStatus([
        LifecycleStatus.REAL,
        LifecycleStatus.CLOSED,
    ]);
    // createdBy is an internal user; its PersonOrg GUID shares the UUID of the InternalUserProfile.
    const { isLoading: isLoadingPersons } = useInternalUserProfilesQuery();
    const knownActorName = useKnownActorName();
    const { data: tenantAccounts, isLoading: isLoadingTenantAccounts } = useListAccountsForTenant();

    // Create account map
    const accountMap = useMemo(
        () =>
            (accounts || []).reduce(
                (map: Map<string, Account>, account: Account) => map.set(account.accountGUID, account),
                new Map<string, Account>(),
            ),
        [accounts],
    );

    // Create person name map

    // Filter requests by tenant if requested
    const filteredRequests = useMemo(() => {
        if (!filterByTenant || !tenantAccounts) {
            return rawInvestmentRequests;
        }

        const allowedPortfolioGuids = new Set(tenantAccounts.map((a: Account) => a.accountGUID));
        return rawInvestmentRequests?.filter((r) => allowedPortfolioGuids.has(r.portfolioGuid));
    }, [rawInvestmentRequests, tenantAccounts, filterByTenant]);

    // Transform investment requests with account and person data
    const investmentRequests = useMemo(() => {
        if (!filteredRequests) return undefined;

        return filteredRequests.map((investmentRequest: InvestmentRequest): InvestmentRequestTableEntry => {
            const account = accountMap.get(investmentRequest.portfolioGuid);
            const { createdBy } = investmentRequest;
            // Sorting, filtering and the CSV export cannot look anyone up as a person; the cell itself names them.
            const createdByName = createdBy ? (knownActorName(createdBy) ?? FORMER_STAFF) : 'System';

            return {
                ...investmentRequest,
                custodyId: account?.custodyAccountId,
                custodyType: account?.custodyType,
                accountName: account?.accountName?.plainText ?? '',
                createdByName,
            };
        });
    }, [filteredRequests, accountMap, knownActorName]);

    // Helper function to get a single investment request by ID
    const getInvestmentRequestById = useMemo(
        () =>
            (requestId: number): InvestmentRequestTableEntry | undefined => {
                return investmentRequests?.find((req) => req.requestId === requestId);
            },
        [investmentRequests],
    );

    // Combined loading state
    const isLoading =
        isLoadingRequests || isLoadingAccounts || isLoadingPersons || (filterByTenant && isLoadingTenantAccounts);

    return {
        investmentRequests,
        getInvestmentRequestById,
        isLoading,
        isUpdating: isFetchingRequests,
        loadingStates: {
            isLoadingRequests,
            isLoadingAccounts,
            isLoadingPersons,
            isLoadingTenantAccounts,
        },
        rawData: {
            accounts,
            accountMap,
            tenantAccounts,
        },
    };
}
